import { EVENTS } from '@/lib/consts.ts'

import { getBusinessStatus } from '@/lib/analytics/helpers.js'

export const gtm1Subscription = {
  preparePayload ({ business, data = {} }) {
    const payload = {
      business_admin_status: getBusinessStatus(business),
      source: 'frontdesk-web',
    }

    const eventAction = data?.event_action || data?.eventAction
    if (eventAction) {
      payload.event_action = eventAction
    }

    return payload
  },

  [EVENTS.SUBSCRIPTION_SECTION_ENTERED] ({ business }) {
    return {
      event: 'Subscription_Section_Entered',
      properties: {
        ...this.preparePayload({ business }),
      },
    }
  },

  [EVENTS.SUBSCRIPTION_OFFER_DISPLAYED] ({ business, data = {} }) {
    return {
      event: 'Subscription_Offer_Displayed',
      properties: {
        ...this.preparePayload({ business }),
        ...(data.entry_source && { entry_source: data.entry_source }),
      },
    }
  },

  [EVENTS.SUBSCRIPTION_OFFER_SUBSCRIBE_CLICKED] ({ business }) {
    return {
      event: 'Subscription_Offer_Subscribe_Clicked',
      properties: {
        ...this.preparePayload({ business }),
      },
    }
  },

  [EVENTS.SUBSCRIPTION_OFFER_SUBSCRIBE_SUCCESS] ({ business }) {
    return {
      event: 'Subscription_Offer_Subscribe_Success',
      properties: {
        ...this.preparePayload({ business }),
      },
    }
  },

  [EVENTS.SUBSCRIPTION_OFFER_SUBSCRIBE_FAILED] ({ data, business }) {
    return {
      event: 'Subscription_Offer_Subscribe_Failed',
      properties: {
        ...this.preparePayload({ business }),
        Error_Message: data?.message,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_PAYMENT_DETAILS_ACTION] ({ data, business }) {
    const {
      eventAction,
      paymentType,
      paymentTypeChanged,
      paymentStatus,
    } = data

    return {
      event: 'SB_Payments_Details_Action',
      properties: {
        ...this.preparePayload({ business }),
        event_action: eventAction,
        payment_type: paymentType,
        payment_type_changed: paymentTypeChanged,
        payment_status: paymentStatus,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_YOUR_SUBSCRIPTION_OPENED] ({ business }) {
    return {
      event: 'Subscription_Your_Subscription_Opened',
      properties: {
        ...this.preparePayload({ business }),
      },
    }
  },

  [EVENTS.SUBSCRIPTION_RENEWED] ({ data, business }) {
    const {
      paymentStatus,
      screenName,
    } = data

    return {
      event: 'SB_Renewed',
      properties: {
        ...this.preparePayload({ business }),
        payment_status: paymentStatus,
        screen_name: screenName,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_NOT_RENEWED] ({ data, business }) {
    const {
      paymentStatus,
      screenName,
    } = data

    return {
      event: 'SB_Not_Renewed',
      properties: {
        ...this.preparePayload({ business }),
        payment_status: paymentStatus,
        screen_name: screenName,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_PAYMENT_METHOD_OPENED] ({ business }) {
    return {
      event: 'Subscription_Payment_Method_Opened',
      properties: {
        ...this.preparePayload({ business }),
      },
    }
  },

  [EVENTS.SUBSCRIPTION_PAYMENT_METHOD_SAVED] ({ data, business }) {
    const {
      paymentStatus,
      screenName,
    } = data

    return {
      event: 'SB_Payment_Method_Saved',
      properties: {
        ...this.preparePayload({ business }),
        payment_status: paymentStatus,
        screen_name: screenName,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_UPDATE_PAYMENT_METHOD_CLICKED] ({ business }) {
    return {
      event: 'SB_Update_Payment_M_Clicked',
      properties: {
        ...this.preparePayload({ business }),
      },
    }
  },

  [EVENTS.SUBSCRIPTION_CHOOSE_PLAN_CLICKED] ({ data, business }) {
    const { availablePlans } = data

    return {
      event: 'Subscription_Choose_Plan_Clicked',
      properties: {
        ...this.preparePayload({ business }),
        available_plans: availablePlans,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_TRIAL_WILL_EXPIRE_SHOWN] ({ data, business }) {
    const {
      day_on_screen,
    } = data

    return {
      event: 'Subscription_Trial_Will_Expire_Shown',
      properties: {
        ...this.preparePayload({ business }),
        day_on_screen: day_on_screen ?? null,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_TRIAL_HAS_EXPIRED_SHOWN] ({ business }) {
    return {
      event: 'Subscription_Trial_Has_Expired_Shown',
      properties: {
        ...this.preparePayload({ business }),
      },
    }
  },
  [EVENTS.SUBSCRIPTION_TRIAL_ACTION] ({ data, business }) {
    const {
      screenName,
      eventAction,
    } = data
    return {
      event: 'Subscription_Trial_Action',
      properties: {
        ...this.preparePayload({ business }),
        screenName,
        eventAction,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_BRAINTREE_ERROR] ({ data, business }) {
    const { error } = data

    return {
      event: 'Subscription_Braintree_Error',
      properties: {
        ...this.preparePayload({ business }),
        error,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_BRAINTREE_TOKENIZE_ERROR] ({ data, business }) {
    const { error } = data

    return {
      event: 'Subscription_Braintree_Tokenize_Error',
      properties: {
        ...this.preparePayload({ business }),
        error,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_BRAINTREE_3DS_VERIFY_ERROR] ({ data, business }) {
    const { error } = data

    return {
      event: 'Subscription_Braintree_3DS_Verify_Error',
      properties: {
        ...this.preparePayload({ business }),
        error,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_ACCOUNT_DISABLED_ACTION] ({ data }) {
    const {
      eventAction,
      paymentType,
    } = data

    return {
      event: 'SB_Account_Disabled_Action',
      properties: {
        event_action: eventAction,
        payment_type: paymentType,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_PAYMENT_FAILED_ACTION] ({ data }) {
    const {
      eventAction,
      paymentType,
    } = data

    return {
      event: 'SB_Payment_Failed_Action',
      properties: {
        event_action: eventAction,
        payment_type: paymentType,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_TRIAL_WILL_EXPIRE_ACTION] ({ data }) {
    const {
      eventAction,
      paymentType,
    } = data

    return {
      event: 'SB_Trial_Will_Expire_Action',
      properties: {
        event_action: eventAction,
        payment_type: paymentType,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_ACCOUNT_DISABLED_RENEW_ACTION] ({ data }) {
    const {
      eventAction,
      paymentType,
    } = data

    return {
      event: 'SB_Account_Disabled_Renew_Action',
      properties: {
        event_action: eventAction,
        payment_type: paymentType,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_YOUR_SUBSCRIPTION_ACTION] ({ data }) {
    const {
      eventAction,
      paymentType,
      discountApplied,
      screenName,
      cancellationProcess,
    } = data

    return {
      event: 'SB_Your_Subscription_Action',
      properties: {
        event_action: eventAction,
        screen_name: screenName ?? null,
        payment_type: paymentType ?? null,
        discount_applied: discountApplied ?? null,
        cancellation_process: cancellationProcess ?? null,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_YOUR_NEXT_PAYMENT_ACTION] ({ data, business }) {
    const {
      paymentType,
      paymentStatus,
    } = data

    return {
      event: 'SB_Your_Next_Payment_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        payment_type: paymentType,
        payment_status: paymentStatus,
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_MESSAGE] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'subscription_offline_migration',
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_OFFER] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'join_subscription_online',
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_OFFER_ACCEPTED] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'join_subscription_online_accepted',
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_PRICE_INFO] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'price_information',
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_EXIT] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'dont_go_yet',
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_AGREEMENTS] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'confirm_and_ready',
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_TERMS] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'privacy_policy',
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_SUCCESS] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'subscription_online_ready',
      },
    }
  },

  [EVENTS.OFFLINE_MIGRATION_ERROR] ({ data, business }) {
    return [
      {
        event: 'Subscription_Online_Action',
        properties: {
          ...this.preparePayload({ data, business }),
          screen_name: 'subscription_online_failed',
        },
      },
      {
        event: 'Subscription_Online_Failed',
        properties: {
          ...this.preparePayload({ data, business }),
          screen_name: 'subscription_online_failed',
        },
      },
    ]
  },

  [EVENTS.OFFLINE_MIGRATION_REMINDER] ({ data, business }) {
    return {
      event: 'Subscription_Online_Action',
      properties: {
        ...this.preparePayload({ data, business }),
        screen_name: 'subscription_offline_migration_reminder',
      },
    }
  },

  [EVENTS.SUBSCRIPTION_PAYMENT_HISTORY_ACTION] ({ data, business }) {
    return {
      event: 'SB_Payment_History_Action',
      properties: {
        business_admin_status: getBusinessStatus(business),
        event_action: data.eventAction,
      },
    }
  },
  [EVENTS.SUBSCRIPTION_RENEW_YOUR_SUBSCRIPTION_ACTION] ({ data }) {
    return {
      event: 'SB_Renew_Your_Subscription_Action',
      properties: {
        payment_status: data.paymentStatus,
        screen_name: data.screenName,
        payment_succeeded: data.paymentSucceeded,
      },
    }
  },

  [EVENTS.SUBSCRIPTION_PAYMENT_METHOD_ACTION] ({ data }) {
    return {
      event: 'SB_Payment_Method_Action',
      properties: {
        payment_status: data.paymentStatus,
        event_action: data.eventAction,
        prev_screen: data.prevScreen || null,
      },
    }
  },

  [EVENTS.BUSINESS_BILLING_ACTION] ({ data }) {
    return {
      event: 'Business_Billing_Action',
      properties: data,
    }
  },
}
